import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, concatMap, forkJoin } from 'rxjs';
import { RoleManagementService } from '../../services/role-management.service';
import { RolesApiActions, RolesPageActions } from './roles.actions';

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

@Injectable()
export class RolesEffects {
  private readonly actions$ = inject(Actions);
  private readonly roleService = inject(RoleManagementService);

  readonly loadRoles$ = createEffect(() => this.actions$.pipe(
    ofType(RolesPageActions.opened, RolesPageActions.reload),
    switchMap(() => this.roleService.getAllRoles().pipe(
      map((roles) => RolesApiActions.loadSucceeded({ roles })),
      catchError((error: { message?: string }) => of(
        RolesApiActions.loadFailed({ error: error?.message ?? 'Unable to load roles' }),
      )),
    )),
  ));

  readonly loadPrivileges$ = createEffect(() => this.actions$.pipe(
    ofType(RolesPageActions.roleSelected),
    switchMap(({ roleId }) => this.roleService.getPrivilegesByRole(roleId).pipe(
      map((privileges) => RolesApiActions.privilegesLoadSucceeded({ roleId, privileges })),
      catchError((error: { message?: string }) => of(
        RolesApiActions.privilegesLoadFailed({ roleId, error: error?.message ?? 'Unable to load role privileges' }),
      )),
    )),
  ));

  readonly createRole$ = createEffect(() => this.actions$.pipe(
    ofType(RolesPageActions.createRequested),
    concatMap(({ requestId, name }) => this.roleService.createRole(name).pipe(
      map((role) => RolesApiActions.createSucceeded({ requestId, role })),
      catchError((error: unknown) => of(RolesApiActions.createFailed({
        requestId,
        error: errorMessage(error, 'Unable to create role'),
      }))),
    )),
  ));

  readonly updateRole$ = createEffect(() => this.actions$.pipe(
    ofType(RolesPageActions.updateRequested),
    concatMap(({ requestId, role }) => this.roleService.updateRole(role).pipe(
      map((updated) => RolesApiActions.updateSucceeded({ requestId, role: updated })),
      catchError((error: unknown) => of(RolesApiActions.updateFailed({
        requestId,
        error: errorMessage(error, 'Unable to update role'),
      }))),
    )),
  ));

  readonly deleteRole$ = createEffect(() => this.actions$.pipe(
    ofType(RolesPageActions.deleteRequested),
    concatMap(({ requestId, roleId }) => this.roleService.deleteRole(roleId).pipe(
      map(() => RolesApiActions.deleteSucceeded({ requestId, roleId })),
      catchError((error: unknown) => of(RolesApiActions.deleteFailed({
        requestId,
        error: errorMessage(error, 'Unable to delete role'),
      }))),
    )),
  ));

  readonly updatePrivileges$ = createEffect(() => this.actions$.pipe(
    ofType(RolesPageActions.updatePrivilegesRequested),
    concatMap(({ requestId, privileges }) => {
      const updates$ = privileges.map(p => this.roleService.updatePrivilege(p));
      return forkJoin(updates$).pipe(
        map(() => RolesApiActions.updatePrivilegesSucceeded({ requestId, privileges })),
        catchError((error: unknown) => of(RolesApiActions.updatePrivilegesFailed({
          requestId,
          error: errorMessage(error, 'Unable to update privileges'),
        }))),
      );
    }),
  ));
}